import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import {
  ConfirmModal,
  Facts,
  Panel,
  RecordHeader,
  RecordTabs,
  StageBar,
  StateView,
  type Fact,
  type HeaderAction,
  type MenuAction,
} from './record'
import { CopyValue, labelOf, listPath } from './recordHelpers'
import type { RecordViewProps } from './types'
import {
  approveStocktake,
  cancelStocktake,
  fetchCountSheet,
  fetchStocktake,
  fetchStocktakeTerminal,
  fetchVariances,
  finishCounting,
  startStocktake,
  type Adjustment,
  type CountSheetLine,
  type Stocktake,
  type Variance,
} from './warehouseApi'
import {
  CellCode,
  HistoryTab,
  OperationAlerts,
  ProductCell,
  RowFilter,
  StatusBadge,
  cancelledEnd,
  copyLinkItem,
  documentState,
  momentOr,
  newOperationKey,
  qty,
  signedQty,
  stagesOf,
  sum,
  useOperation,
} from './warehouseRecord'
import '@/styles/record.css'

const STAGES = ['draft', 'in_progress', 'counted', 'approved'] as const

/**
 * Карточка инвентаризации на дизайн-системе (Inventory.dc.html).
 *
 * ## Слепой пересчёт
 *
 * Ожидаемое количество при слепом пересчёте подавляет **сервер** (`expected == null`), а
 * этот экран его **не показывает ни в каком виде**: в листе нет колонки «Ожидается» вовсе,
 * ни прочерком, ни нулём, а сличительная ведомость (где учётная цифра неизбежна) до
 * завершения пересчёта не запрашивается и вкладкой не показывается. Офисный лист слеп так
 * же, как терминальный: заведующий, подсказавший цифру по телефону, отменил бы пересчёт
 * целиком — счётчик, увидевший цифру, находит примерно её и останавливается.
 *
 * ## Действия
 *
 * Подготовку передают в пересчёт, пересчёт завершают (оба — `update`, `/wms/stocktakes/...`),
 * результаты утверждают **отдельным действием и отдельным правом `approve`** через
 * подтверждение: завершение фиксирует, что посчитали, а утверждение меняет остаток — деньги
 * на балансе. Корректировка идёт разницей к текущему остатку; это делает сервер, экран
 * показывает результат — было, стало, разница.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - Комиссия и подписи, приказ, председатель, лента и комментарии: API их не публикует.
 * - Учётные цены и разница в рублях: ведомость сервера — в штуках.
 * - Кто и когда считал ячейку, ввод количества в офисе, «Пересчитать строки», «Печать
 *   листов», «Выбрать ячейки», экспорт ИНВ-3/ИНВ-19: пересчёт — работа терминала, а
 *   остальных операций нет.
 * - Закрытие зоны для отбора на время пересчёта: такого признака в документе нет.
 */
export function StocktakeRecordView({ recordId, record, form, notices }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const [tab, setTab] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'pending'>('all')
  const [confirm, setConfirm] = useState<'approve' | 'cancel' | null>(null)
  const [adjustments, setAdjustments] = useState<Adjustment[] | null>(null)
  const operation = useOperation([
    ['stocktake', recordId],
    ['stocktake-sheet', recordId],
    ['stocktake-variances', recordId],
    ['record', 'warehouse.stocktake', recordId],
    ['list', 'warehouse.stocktake'],
    ['list', 'warehouse.stock'],
  ])

  const mayUpdate = can(user, 'warehouse.stocktake', 'update')
  const mayApprove = can(user, 'warehouse.stocktake', 'approve')
  const mayCancel = can(user, 'warehouse.stocktake', 'delete')

  const stocktakeQuery = useQuery({
    queryKey: ['stocktake', recordId],
    queryFn: ({ signal }) => fetchStocktake(recordId, signal),
    retry: false,
  })
  const sheet = useQuery({
    queryKey: ['stocktake-sheet', recordId],
    queryFn: ({ signal }) => fetchCountSheet(recordId, signal),
    retry: false,
  })
  const terminal = useQuery({
    queryKey: ['stocktake-terminal', recordId],
    queryFn: ({ signal }) => fetchStocktakeTerminal(recordId, signal),
    retry: false,
  })
  const status = stocktakeQuery.data?.status
  // The variance act carries the book figure. Before counting is finished it is neither
  // requested nor shown: for a blind count that figure is exactly what must not be seen.
  const compared = status === 'counted' || status === 'approved'
  const variances = useQuery({
    queryKey: ['stocktake-variances', recordId],
    queryFn: ({ signal }) => fetchVariances(recordId, signal),
    enabled: compared,
    retry: false,
  })

  const start = useMutation({
    mutationFn: (key: string) => startStocktake(recordId, key),
    onSuccess: () => operation.succeed(t('record.wh.st.started')),
    onError: operation.fail,
  })
  const finish = useMutation({
    mutationFn: (key: string) => finishCounting(recordId, key),
    onSuccess: () => {
      setTab('variances')
      operation.succeed(t('record.wh.st.finished'))
    },
    onError: operation.fail,
  })
  const approve = useMutation({
    mutationFn: () => approveStocktake(recordId),
    onSuccess: (result) => {
      setConfirm(null)
      setAdjustments(result)
      operation.succeed(t('card.wh.stocktake.approved', { count: result.length }))
    },
    onError: (error) => {
      setConfirm(null)
      operation.fail(error)
    },
  })
  const cancel = useMutation({
    mutationFn: () => cancelStocktake(recordId),
    onSuccess: () => {
      setConfirm(null)
      operation.succeed(t('card.wh.stocktake.cancelled'))
    },
    onError: (error) => {
      setConfirm(null)
      operation.fail(error)
    },
  })

  const gate = documentState(stocktakeQuery, 'warehouse.stocktake', '/stocktakes', t)
  if (gate) return gate
  const stocktake = stocktakeQuery.data as Stocktake

  const names = terminal.data?.catalogue?.names ?? {}
  const lines = sheet.data ?? []
  const uncounted = lines.filter((line) => line.counted == null).length
  const counted = lines.length - uncounted
  const rows = variances.data ?? []
  const shortages = rows.filter((row) => !row.surplus)
  const surpluses = rows.filter((row) => row.surplus)
  const activeTab = tab ?? (compared ? 'variances' : 'sheet')
  const open = stocktake.status !== 'approved' && stocktake.status !== 'cancelled'

  // ------------------------------------------------------------------ шапка

  const warehouseLabel = labelOf(record.values, 'warehouse_id')
  const zoneLabel = labelOf(record.values, 'zone_id')
  const scopeText = `${t(`value.warehouse.stocktake.scope.${stocktake.scope}`)}${zoneLabel ? ` ${zoneLabel}` : ''}`
  const meta = [
    <StatusBadge key="status" entity="warehouse.stocktake" status={stocktake.status} />,
    stocktake.blind ? (
      <span key="blind" className="badge">
        <Icon name="lock" />
        {t('card.wh.stocktake.blind')}
      </span>
    ) : null,
    warehouseLabel ? (
      <span key="warehouse" className="row" style={{ gap: 6 }}>
        <Icon name="box" size="sm" className="muted" />
        {t('record.wh.warehouseNamed', { name: warehouseLabel })}
      </span>
    ) : null,
    <span key="scope">{scopeText}</span>,
    <CopyValue key="copy" value={stocktake.number} label={t('record.copyNumber', { number: stocktake.number })} />,
  ]

  let primary: HeaderAction | null = null
  if (stocktake.status === 'draft' && mayUpdate) {
    primary = {
      id: 'start',
      label: t('record.wh.st.start'),
      icon: 'arrow-right',
      disabled: stocktake.lineCount === 0 || start.isPending,
      onClick: () => {
        operation.clear()
        start.mutate(newOperationKey())
      },
    }
  } else if (stocktake.status === 'in_progress' && mayUpdate) {
    primary = {
      id: 'finish',
      label: t('record.wh.st.finish'),
      icon: 'check',
      disabled: (sheet.data !== undefined && uncounted > 0) || finish.isPending,
      onClick: () => {
        operation.clear()
        finish.mutate(newOperationKey())
      },
    }
  } else if (stocktake.status === 'counted' && stocktake.approvedAt == null && mayApprove) {
    primary = {
      id: 'approve',
      label: t('record.wh.st.approve'),
      onClick: () => {
        operation.clear()
        setConfirm('approve')
      },
    }
  }

  const menu: MenuAction[] = [copyLinkItem(t, operation.setFailure)]
  if (mayCancel && open) {
    menu.push({
      id: 'cancel',
      label: t('card.wh.stocktake.cancel'),
      icon: 'close',
      danger: true,
      onSelect: () => {
        operation.clear()
        setConfirm('cancel')
      },
    })
  }

  // ------------------------------------------------------------------ факты

  const hidden = (
    <span className="row" style={{ gap: 4, color: 'var(--fg-muted)', fontWeight: 500 }}>
      <Icon name="lock" size="sm" />
      {t('record.wh.st.hidden')}
    </span>
  )
  const facts: Fact[] = [
    {
      id: 'positions',
      label: t('record.wh.st.positions'),
      value: qty(sheet.data ? lines.length : stocktake.lineCount, locale),
      sub: scopeText,
    },
    {
      id: 'counted',
      label: t('card.wh.stocktake.counted'),
      value: sheet.data ? t('record.wh.of', { done: counted, total: lines.length }) : '—',
      sub: compared
        ? t('record.wh.st.countedAt', { at: momentOr(record.values.completed_at, locale, '—') })
        : uncounted > 0
          ? t('record.wh.st.left', { count: uncounted })
          : undefined,
    },
    {
      id: 'variances',
      label: t('record.wh.st.variances'),
      value: compared ? (
        variances.isError ? (
          '—'
        ) : rows.length > 0 ? (
          <span className="flag flag-danger" style={{ fontSize: 16, fontWeight: 600 }}>
            <Icon name="alert" />
            {qty(rows.length, locale)}
          </span>
        ) : (
          <span className="muted">{variances.isLoading ? '…' : '0'}</span>
        )
      ) : (
        hidden
      ),
      sub: compared
        ? t('record.wh.st.variancesSub', { shortages: shortages.length, surpluses: surpluses.length })
        : t('record.wh.st.hiddenUntil'),
    },
    {
      id: 'started',
      label: t('record.wh.st.startedAt'),
      value: <span className="t-md" style={{ fontWeight: 500 }}>{momentOr(record.values.started_at, locale, t('record.wh.st.notStarted'))}</span>,
    },
    {
      id: 'approved',
      label: t('card.wh.stocktake.approvedAt'),
      value: <span className="t-md" style={{ fontWeight: 500 }}>{momentOr(stocktake.approvedAt, locale, t('card.wh.stocktake.notApproved'))}</span>,
    },
  ]

  // ------------------------------------------------------------------ вкладки

  const visible = filter === 'pending' ? lines.filter((line) => line.counted == null) : lines
  const sheetPanel = (
    <>
      {stocktake.blind && (
        // Blindness is named on screen: otherwise the missing column reads as a broken
        // load, and somebody goes to fix it.
        <div className="alert alert-neutral" role="status">
          <Icon name="lock" />
          <span>{t('card.wh.stocktake.blindNotice')}</span>
        </div>
      )}
      {sheet.isLoading ? (
        <p className="muted">{t('app.loading')}</p>
      ) : sheet.isError ? (
        <StateView
          boxed
          kind={sheet.error instanceof ApiError && sheet.error.isForbidden ? 'forbidden' : 'error'}
          title={t('record.wh.st.sheetError')}
        />
      ) : lines.length === 0 ? (
        <StateView boxed kind="empty" title={t('card.wh.stocktake.noLines')} description={t('record.wh.st.emptyText')} />
      ) : (
        <Panel
          title={t('card.tab.whCountSheet')}
          count={lines.length}
          flush
          actions={
            <RowFilter
              label={t('record.wh.rowFilter')}
              value={filter}
              onChange={setFilter}
              options={[
                { id: 'all', label: t('record.wh.filter.all') },
                { id: 'pending', label: t('record.wh.st.filterUncounted'), count: uncounted },
              ]}
            />
          }
        >
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl" aria-label={t('card.tab.whCountSheet')}>
              <thead>
                <tr>
                  <th>{t('card.wh.line.cell')}</th>
                  <th>{t('card.wh.line.product')}</th>
                  {/* No "expected" column for a blind count at all — not a dash, not a zero. */}
                  {!stocktake.blind && <th className="num">{t('card.wh.stocktake.expected')}</th>}
                  <th className="num">{t('card.wh.stocktake.counted')}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((line) => (
                  <SheetRow key={line.lineId} line={line} blind={stocktake.blind} name={line.productName ?? names[line.productId]} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="panel-f secondary">
            {compared ? t('record.wh.st.sheetDone') : t('record.wh.st.sheetNote')}
          </div>
        </Panel>
      )}
    </>
  )

  const variancePanel = (
    <>
      {adjustments != null && <AdjustmentsPanel rows={adjustments} names={names} />}
      <VariancePanel query={variances} names={names} />
    </>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[
          { label: t('nav.stocktakes'), to: listPath('warehouse.stocktake', '/stocktakes') },
          { label: stocktake.number, mono: true },
        ]}
        title={
          <>
            {t('record.wh.st.title')} <span className="mono rec-mono-title">{stocktake.number}</span>
          </>
        }
        meta={meta}
        primary={primary}
        menu={menu}
        menuLabel={t('record.more')}
      />

      <OperationAlerts failure={operation.failure} notice={operation.notice} />
      {notices}
      {primary?.id === 'finish' && primary.disabled && uncounted > 0 && (
        <div className="alert alert-neutral" role="status">
          <Icon name="clock" />
          <span>{t('record.wh.st.finishBlocked', { count: uncounted })}</span>
        </div>
      )}
      {primary?.id === 'start' && stocktake.lineCount === 0 && (
        <div className="alert alert-neutral" role="status">
          <Icon name="inbox" />
          <span>{t('record.wh.st.startBlocked')}</span>
        </div>
      )}
      {stocktake.status === 'counted' && stocktake.approvedAt == null && !mayApprove && (
        // Said, not hidden: the storekeeper has to know the document waits for the head,
        // not for him.
        <div className="alert alert-neutral" role="status">
          <Icon name="lock" />
          <span>{t('card.wh.stocktake.needsApproveRight')}</span>
        </div>
      )}

      <StageBar
        ariaLabel={t('record.wh.stages')}
        steps={stagesOf(
          STAGES,
          stocktake.status === 'cancelled' ? -1 : STAGES.indexOf(stocktake.status as (typeof STAGES)[number]),
          (code) => t(`record.wh.st.stage.${code}`),
          (code) =>
            code === 'draft'
              ? momentOr(record.values.created_at, locale, '')
              : code === 'in_progress'
                ? momentOr(record.values.started_at, locale, '')
                : code === 'counted'
                  ? momentOr(record.values.completed_at, locale, '')
                  : momentOr(stocktake.approvedAt, locale, t('record.wh.st.stage.changesStock')),
        )}
        ends={cancelledEnd(stocktake.status === 'cancelled', t('record.wh.cancelledEnd'))}
      />

      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          ...(compared ? [{ id: 'variances', label: t('card.tab.whVariances'), count: variances.data ? rows.length : null }] : []),
          { id: 'sheet', label: t('card.tab.whCountSheet'), count: sheet.data ? lines.length : null },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={activeTab}
        onChange={setTab}
        ariaLabel={t('record.wh.st.sections')}
      >
        {activeTab === 'history' ? (
          <HistoryTab entity="warehouse.stocktake" recordId={recordId} feedType="stocktake" />
        ) : (
          <div className="layout-2-wide" style={{ paddingTop: 14 }}>
            <div className="rec-col">
              {activeTab === 'variances' && compared ? variancePanel : sheetPanel}
              {!compared && <p className="secondary t-sm" style={{ margin: 0 }}>{t('record.wh.st.varianceLater')}</p>}
            </div>
            <div className="rec-col">
              <Panel title={t('record.wh.details')}>{form}</Panel>
            </div>
          </div>
        )}
      </RecordTabs>

      <ConfirmModal
        open={confirm === 'approve'}
        icon="alert"
        title={t('record.wh.st.confirm.title')}
        confirmLabel={t('record.wh.st.confirm.action')}
        pending={approve.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => approve.mutate()}
      >
        <p>{t('card.wh.stocktake.approveWarning')}</p>
        {rows.length > 0 ? (
          <>
            <p>
              {t('record.wh.st.confirm.totals', {
                warehouse: warehouseLabel ?? '—',
                shortage: signedQty(sum(shortages, (row) => row.variance), locale),
                surplus: signedQty(sum(surpluses, (row) => row.variance), locale),
              })}
            </p>
            <dl className="kv wh-confirm-kv" style={{ margin: 0 }}>
              {rows.map((row, index) => (
                <div key={`${row.productId}-${row.cellId}-${index}`} style={{ display: 'contents' }}>
                  <dt className="mono">{row.cellId ? (row.cellCode ?? row.cellId.slice(0, 8)) : '—'}</dt>
                  <dd>{row.productName ?? names[row.productId] ?? row.productId.slice(0, 8)}</dd>
                  <dd className="num">{signedQty(row.variance, locale)}</dd>
                </div>
              ))}
            </dl>
          </>
        ) : (
          <p>{t('record.wh.st.confirm.noVariances')}</p>
        )}
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.wh.st.confirm.final')}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={confirm === 'cancel'}
        icon="close"
        title={t('record.wh.st.cancel.title', { number: stocktake.number })}
        confirmLabel={t('card.wh.stocktake.cancel')}
        pending={cancel.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => cancel.mutate()}
      >
        <p>{t('record.wh.st.cancel.text')}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>
    </div>
  )
}

/** Строка листа. При слепом пересчёте ячейки «ожидается» нет вовсе — ни прочерка, ни нуля. */
function SheetRow({
  line,
  blind,
  name,
}: {
  line: CountSheetLine
  blind: boolean
  name: string | undefined
}) {
  const { t, locale } = useApp()
  return (
    <tr>
      <td>
        <CellCode id={line.cellId} code={line.cellCode} />
      </td>
      <td className="wrap">
        <ProductCell id={line.productId} name={name} sku={line.sku} />
      </td>
      {!blind && <td className="num">{qty(line.expected, locale)}</td>}
      <td className="num">
        {line.counted == null ? (
          <span className="flag flag-warning">
            <Icon name="clock" />
            {t('record.wh.st.notCounted')}
          </span>
        ) : (
          <span className="strong">{qty(line.counted, locale)}</span>
        )}
      </td>
    </tr>
  )
}

type VarianceQuery = { isLoading: boolean; isError: boolean; error: unknown; data: Variance[] | undefined }

/**
 * Сличительная ведомость. Излишек и недостача различаются **словом**, а не только знаком:
 * недостача — вопрос к людям, излишек — к учёту.
 */
function VariancePanel({
  query,
  names,
}: {
  query: VarianceQuery
  names: Record<string, string>
}) {
  const { t, locale } = useApp()
  if (query.isLoading) return <p className="muted">{t('app.loading')}</p>
  if (query.isError) {
    return (
      <StateView
        boxed
        kind={query.error instanceof ApiError && query.error.isForbidden ? 'forbidden' : 'error'}
        title={t('record.wh.st.varianceError')}
      />
    )
  }
  const rows = query.data ?? []
  // "No variances" is the outcome of the count, not missing data.
  if (rows.length === 0) return <StateView boxed kind="empty" title={t('card.wh.stocktake.noVariances')} />
  const shortages = rows.filter((row) => !row.surplus)
  const surpluses = rows.filter((row) => row.surplus)
  return (
    <Panel title={t('card.tab.whVariances')} count={rows.length} flush actions={<span className="t-sm muted">{t('record.wh.st.inPieces')}</span>}>
      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" aria-label={t('card.tab.whVariances')}>
          <thead>
            <tr>
              <th>{t('card.wh.line.cell')}</th>
              <th>{t('card.wh.line.product')}</th>
              <th className="num">{t('card.wh.stocktake.expected')}</th>
              <th className="num">{t('card.wh.stocktake.counted')}</th>
              <th className="num">{t('card.wh.stocktake.variance')}</th>
              <th>{t('card.wh.stocktake.comment')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.productId}-${row.cellId}-${index}`}>
                <td>
                  <CellCode id={row.cellId} code={row.cellCode} />
                </td>
                <td className="wrap">
                  <ProductCell id={row.productId} name={row.productName ?? names[row.productId]} sku={row.sku} />
                </td>
                <td className="num">{qty(row.expected, locale)}</td>
                <td className="num">{qty(row.counted, locale)}</td>
                <td className="num">
                  <span className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                    {row.surplus ? (
                      <span className="flag flag-warning">
                        <Icon name="plus" />
                        {t('card.wh.stocktake.surplus')}
                      </span>
                    ) : (
                      <span className="flag flag-danger">
                        <Icon name="alert" />
                        {t('card.wh.stocktake.shortage')}
                      </span>
                    )}
                    <span className="strong">{signedQty(row.variance, locale)}</span>
                  </span>
                </td>
                <td className="wrap">{row.comment ?? '—'}</td>
              </tr>
            ))}
            <tr className="total">
              <td colSpan={4}>{t('record.wh.st.shortageTotal', { count: shortages.length })}</td>
              <td className="num">{signedQty(sum(shortages, (row) => row.variance), locale)}</td>
              <td />
            </tr>
            <tr className="total">
              <td colSpan={4}>{t('record.wh.st.surplusTotal', { count: surpluses.length })}</td>
              <td className="num">{signedQty(sum(surpluses, (row) => row.variance), locale)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

/**
 * Проведённые корректировки — результат утверждения: было, стало, разница. «Утверждено» без
 * цифр не отвечает на вопрос, что изменилось на балансе.
 */
function AdjustmentsPanel({
  rows,
  names,
}: {
  rows: Adjustment[]
  names: Record<string, string>
}) {
  const { t, locale } = useApp()
  return (
    <Panel title={t('card.wh.stocktake.adjustments')} count={rows.length} flush>
      {rows.length === 0 ? (
        // Zero adjustments is a normal outcome: the count matched. It must differ from
        // "not approved".
        <div className="panel-b secondary">{t('card.wh.stocktake.noAdjustments')}</div>
      ) : (
        <table className="tbl" aria-label={t('card.wh.stocktake.adjustments')}>
          <thead>
            <tr>
              <th>{t('card.wh.line.cell')}</th>
              <th>{t('card.wh.line.product')}</th>
              <th className="num">{t('card.wh.stocktake.before')}</th>
              <th className="num">{t('card.wh.stocktake.after')}</th>
              <th className="num">{t('card.wh.stocktake.delta')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.productId}-${row.cellId}-${index}`}>
                <td>
                  <CellCode id={row.cellId} code={row.cellCode} />
                </td>
                <td className="wrap">
                  <ProductCell id={row.productId} name={row.productName ?? names[row.productId]} sku={row.sku} />
                </td>
                <td className="num">{qty(row.before, locale)}</td>
                <td className="num">{qty(row.after, locale)}</td>
                {/* The difference, not the new value: the adjustment is applied to the live
                    balance. */}
                <td className="num strong">{signedQty(row.delta, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )
}
